import React, {useEffect, useRef} from "react"
import { Outlet, useLocation } from "react-router-dom"
import { Row, Col, Spin, Steps, Layout, theme } from "antd"

import Error from "@modules/investor/commitment/Error"
import ContactUs from "@components/ContactUs"
import PlatformFooter from "@components/PlatformFooter"
import InvestmentHeader from "@components/InvestmentHeader"
import { useAppContext } from "@components/AppContext"
import ImpersonatedAccountAlert from "@layouts/investor/InvestorLayout/ImpersonatedAccountAlert"
import { W1, W2, W3, W5, isMobile } from "@components/constants"
import useProjectInvestmentsStore from "@stores/investor/useProjectInvestmentsStore"
import useTransactionsStore from "@stores/investor/useTransactionsStore"

import Header from "./Header"
import getStepsMetadata from "./helpers/getStepsMetadata"


const InvestorInvestmentLayout = () => {
  const currentForm = useRef({})

  const { token } = theme.useToken()
  const { pathname } = useLocation()

  const { request, identity } = useAppContext()
  const projectInvestmentsStore = useProjectInvestmentsStore(request)
  const transactionsStore = useTransactionsStore(request)

  if (!identity) {
    return null
  }

  const { organization } = identity
  const { logoUrl, name: organizationName } = organization

  const { error } = projectInvestmentsStore
  const { project, investments } = projectInvestmentsStore
  const { transactions } = transactionsStore

  const subtitle = (project?.name || "").toUpperCase()

  const {
    stepsItems,
    currentStep,
    currentText,
    currentTitle,
  } = getStepsMetadata(identity, pathname)

  useEffect(() => {
    document
      .querySelector('meta[name="viewport"]')
      .setAttribute("content", "width=device-width, initial-scale=1, maximum-scale=1")
  }, [])

  const isReady = !!project && !!investments && !!transactions

  const isLoading = !error && !project
  const stepsSize = "default"
  const { colorBgContainer, colorBgLayout } = token

  const layoutStyle = {
    marginBottom: isMobile ? W5 * 3 : W2,
    backgroundColor: colorBgContainer
  }

  const headerStyle = {
    top: 0,
    width: "100%",
    zIndex: 1,
    padding: 0,
    position: "sticky",
    borderBottom: "1px solid rgba(0,0,0,0.1)",
    backgroundColor: colorBgLayout,
  }

  const contentStyle = {
    padding: W1,
    paddingTop: isMobile ? W3 : W1,
  }

  if (!isMobile) {
    contentStyle.minHeight = "60vh"
  }

  const pageHeaderStyle = {
    marginBottom: W1 * 1.5
  }

  const context = {
    currentForm,
    ...transactionsStore,
    ...projectInvestmentsStore
  }

  const headerProps = {
    logoUrl,
    currentForm,
    organizationName,
  }

  const stepsProps = {
    size: stepsSize,
    items: stepsItems,
    current: currentStep,
  }

  const investmentHeaderProps = {
    text: currentText,
    title: currentTitle,
    style: pageHeaderStyle,
    titleLevel: 2,
    subtitle,
  }

  return (
    <Spin spinning={isLoading}>
      <Layout style={layoutStyle}>
        {
          isReady && (
            <>
              <div className="header-alerts">
                <ImpersonatedAccountAlert />
              </div>

              <Layout.Header style={headerStyle}>
                <Header {...headerProps} />
              </Layout.Header>

              <Layout.Content style={contentStyle}>
                {
                  !isMobile && (
                    <Row justify="center" style={{ marginBottom: W2 }}>
                      <Col style={{ maxWidth: 640, width: "100%" }}>
                        <Steps {...stepsProps} />
                      </Col>
                    </Row>
                  )
                }

                <Row justify="center">
                  <Col style={{ maxWidth: 520 }}>
                    <InvestmentHeader {...investmentHeaderProps} />
                    <Outlet context={context} />
                  </Col>
                </Row>
              </Layout.Content>
            </>
          )
        }

        {
          error && (
            <Row justify="center" style={{ marginTop: W2 }}>
              <Col style={{ maxWidth: 520 }}>
                <Error
                  title={error.title}
                  message={error.message}
                />
              </Col>
            </Row>
          )
        }

        <ContactUs />

        <PlatformFooter />
      </Layout>
    </Spin>
  )
}

export default InvestorInvestmentLayout
